<template>
  <div
    :id="rootId"
    :class="rootClass"
    :style="rootStyle"
  >
    <div
      ref="root"
      class="gmap-map__canvas"
    ></div>
    <slot />
  </div>
</template>

<script>
import { nextTick } from 'vue'
import { loadGoogleMaps } from './googleMapsLoader'

export default {
  name: 'GmapMap',
  inheritAttrs: false,
  props: {
    center: {
      type: Object,
      default: () => ({ lat: -23.5505199, lng: -46.63330940000003 })
    },
    zoom: {
      type: Number,
      default: 5
    },
    mapTypeId: {
      type: String,
      default: 'roadmap'
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      google: null,
      map: null,
      mapPromise: null,
      mapListeners: [],
      resolveMapPromise: null,
      rejectMapPromise: null
    }
  },
  created () {
    this.mapPromise = new Promise((resolve, reject) => {
      this.resolveMapPromise = resolve
      this.rejectMapPromise = reject
    })
  },
  computed: {
    rootClass () {
      return this.$attrs.class
    },
    rootStyle () {
      return this.$attrs.style
    },
    rootId () {
      return this.$attrs.id
    }
  },
  provide () {
    return {
      gmapContext: {
        getMapPromise: () => this.mapPromise,
        getGoogle: () => this.google
      }
    }
  },
  watch: {
    center: {
      deep: true,
      handler (value) {
        if (!value || !this.mapPromise) return
        this.mapPromise.then((map) => map.setCenter(value))
      }
    },
    zoom (value) {
      if (!this.mapPromise || typeof value !== 'number') return
      this.mapPromise.then((map) => map.setZoom(value))
    },
    mapTypeId (value) {
      if (!this.mapPromise || !value) return
      this.mapPromise.then((map) => map.setMapTypeId(value))
    },
    options: {
      deep: true,
      handler (value) {
        if (!this.mapPromise) return
        this.mapPromise.then((map) => map.setOptions(value || {}))
      }
    }
  },
  async mounted () {
    try {
      await this.mountMap()
    } catch (error) {
      if (this.rejectMapPromise) {
        this.rejectMapPromise(error)
      }
      throw error
    }
  },
  beforeUnmount () {
    this.clearMapListeners()
  },
  methods: {
    getMapPromise () {
      return this.mapPromise
    },
    bindMapListeners () {
      if (!this.map) return

      this.mapListeners.push(
        this.map.addListener('dblclick', (event) => {
          this.$emit('dblclick', event)
        })
      )
    },
    async mountMap () {
      await nextTick()

      const google = await loadGoogleMaps()

      this.google = google
      this.map = new google.maps.Map(this.$refs.root, {
        center: this.center,
        zoom: this.zoom,
        mapTypeId: this.mapTypeId,
        ...this.options
      })
      this.bindMapListeners()
      if (this.resolveMapPromise) {
        this.resolveMapPromise(this.map)
      }
      this.$emit('ready', this.map)
      return this.map
    },
    clearMapListeners () {
      this.mapListeners.forEach((listener) => listener.remove())
      this.mapListeners = []
    }
  }
}
</script>

<style scoped>
.gmap-map__canvas {
  width: 100%;
  height: 100%;
  min-height: inherit;
}
</style>
